import { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldPath, UseFormReturn } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import { Plus } from 'lucide-react';

import { Checkbox } from 'components/Checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { RHFDebounceSelect } from 'components/input/RHFDebounceSelect';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { getLocationDetails } from 'lib/api/getLocationDetails';
import { getLocations } from 'lib/api/getLocations';
import { refreshLocationsGlobalTranz } from 'lib/api/searchLocationGlobalTranz';
import { LoadDateInput } from 'pages/LoadView/LoadInformation/Components';
import {
  LoadStopTypes,
  NormalizedLoad,
  TMSCustomer,
  TMSLocation,
} from 'types/Load';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import {
  GenericCompanySearchableFields,
  injectSelectedObject,
  locationSearchHandler,
  mapLocationsToSelectOptions,
} from 'utils/loadInfoAndBuilding';

import LocationCreatorDialog from './components/LocationCreatorDialog';
import { generateTimeOptions, getDefaultTimes, toHHMMString } from './utils';

/**
 * Converts a time value (Date object or string) to HH:MM format string for display.
 * Reuses toHHMMString for Date/ISO; normalizes HH:MM strings to two-digit format so display matches submission.
 */
function formatTimeForSelect(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{1,2}:\d{2}$/.test(value)) {
    const [h, m] = value.split(':');
    return `${(h ?? '').padStart(2, '0')}:${(m ?? '').padStart(2, '0')}`;
  }
  return toHHMMString(value as MaybeUndef<Date | string>);
}

/** Parse HH:MM to minutes since midnight for comparison */
function parseHHMMToMinutes(hhmm: string): number {
  const match = hhmm?.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return 0;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

export function StopForm({
  stop,
  formMethods,
  isLoadingLocations,
  locations,
  handleRefreshLocations,
  originalSuggestionData: _originalSuggestionData,
  tmsID,
  customerID,
  setLocations,
  initialCustomers,
  initialIsLoadingCustomers,
  apptConfirmedWithLocationFromLoad = false,
  fetchLocations,
  isCreateMode = false,
}: {
  stop: 'pickup' | 'consignee';
  formMethods: UseFormReturn<NormalizedLoad>;
  isLoadingLocations: boolean;
  locations: Maybe<TMSLocation[]>;
  handleRefreshLocations: () => void;
  originalSuggestionData?: NormalizedLoad;
  tmsID?: number;
  // Internal numeric DB id of the selected customer; passed to getLocationDetails
  customerID?: number;
  setLocations?: (locations: TMSLocation[]) => void;
  // Customer list from main form so Create Location modal uses same list as Customer dropdown
  initialCustomers?: Maybe<TMSCustomer[]>;
  initialIsLoadingCustomers?: boolean;
  // When true, field had value when load was loaded (from save/site); checkbox disabled when checked so user cannot uncheck
  apptConfirmedWithLocationFromLoad?: boolean;
  isCreateMode?: boolean;
  // fetchLocations(false): reads from RDS so the location just synced by refreshLocationsGlobalTranz
  // is visible in the dropdown, and preserves existing options on a transient error instead of wiping
  // the list. See fetchLocations in UnifiedGlobalTranzForm for full details.
  fetchLocations?: () => void;
}): JSX.Element {
  const [isEditingLocation] = useState(false);
  const [filteredLocations, setFilteredLocations] =
    useState<Maybe<TMSLocation[]>>(locations);
  const [isLocationCreatorOpen, setIsLocationCreatorOpen] = useState(false);
  const locationDetailsFetchInFlightRef = useRef<Maybe<string>>(null);
  // Tracks the latest search invocation to discard stale fallback-refresh results
  const searchCounterRef = useRef(0);
  // When true, the next locations-prop sync effect is skipped (used when we intentionally
  // update the parent's locations list but want to keep the filtered dropdown view intact)
  const skipNextLocationsSyncRef = useRef(false);

  const {
    control,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = formMethods;

  const watchedOpenTime = watch(
    `${stop}.openTime` as FieldPath<NormalizedLoad>
  );
  const watchedCloseTime = watch(
    `${stop}.closeTime` as FieldPath<NormalizedLoad>
  );
  const watchedLocationID = watch(`${stop}.externalTMSID`);
  const watchedLocObj = watch(stop);
  const watchedCustomer = watch('customer');
  const lastSelectedLocationIdRef = useRef<Maybe<string>>(null);
  // Track the previous location ID to detect when selection just changed.
  // Used to skip the field-mismatch check during the hydration window.
  const prevLocationIDForMismatchRef = useRef<Maybe<string>>(null);
  const { tmsIntegrations } = useServiceFeatures();
  const { toast } = useToast();
  const { aiDefaultValues } = useExtendedFormContext();

  const stopTypeEnum =
    stop === LoadStopTypes.Pickup
      ? LoadStopTypes.Pickup
      : LoadStopTypes.Consignee;
  const openTimeOptions = useMemo(
    () => generateTimeOptions(stopTypeEnum, false),
    [stopTypeEnum]
  );
  const closeTimeOptions = useMemo(
    () => generateTimeOptions(stopTypeEnum, true),
    [stopTypeEnum]
  );

  useEffect(() => {
    // we should skip the next full locations sync if the
    // user has just finished a search
    if (skipNextLocationsSyncRef.current) {
      skipNextLocationsSyncRef.current = false;
      return;
    }
    setFilteredLocations(locations);
  }, [locations]);

  const handleFilteredLocationsChange: React.Dispatch<
    React.SetStateAction<Maybe<TMSLocation[]>>
  > = (updatedLocations) => {
    setFilteredLocations((prev) => {
      const externalTmsId = String(
        getValues(`${stop}.externalTMSID` as FieldPath<NormalizedLoad>)
      );

      return withCurrentSelectionInSearchResults(
        updatedLocations as Maybe<TMSLocation[]>,
        prev,
        locations,
        externalTmsId
      );
    });
  };

  const effectiveTmsID = tmsID ?? tmsIntegrations?.[0]?.id;

  const handleLocationSearch = async (
    field: GenericCompanySearchableFields,
    value: string
  ) => {
    searchCounterRef.current += 1;
    const thisSearchId = searchCounterRef.current;

    const customerExternalTmsId = (watchedCustomer as any)
      ?.externalTMSID as Undef<string>;
    const effectiveTmsIDForSearch = tmsID ?? tmsIntegrations?.[0]?.id;
    if (!effectiveTmsIDForSearch) {
      return [];
    }

    const results = await locationSearchHandler({
      tmsID: effectiveTmsIDForSearch,
      locations,
      setLocations: handleFilteredLocationsChange,
      field,
      value,
      customerExternalTmsId,
    });

    // When the backend returns no results for a substantive search term, auto-trigger
    // a GTZ address book refresh to seed the location into RDS, then re-fetch and
    // return any newly matched locations so results appear without a second search.
    if (
      results.length === 0 &&
      value.length > 2 &&
      customerExternalTmsId &&
      effectiveTmsIDForSearch
    ) {
      const refreshRes = await refreshLocationsGlobalTranz(
        effectiveTmsIDForSearch,
        customerExternalTmsId,
        value
      );

      if (refreshRes.isOk()) {
        const locRes = await getLocations(
          effectiveTmsIDForSearch,
          false,
          false,
          customerExternalTmsId
        );

        if (locRes.isOk()) {
          const freshLocations = locRes.value.locationList;
          const searchTerm = value.toLowerCase();
          const matched = freshLocations.filter((loc) => {
            const searchableText =
              `${loc.name ?? ''} ${loc.addressLine1 ?? ''}`.toLowerCase();
            return searchableText.includes(searchTerm);
          });

          if (
            matched.length > 0 &&
            thisSearchId === searchCounterRef.current &&
            (getValues('customer') as any)?.externalTMSID ===
              customerExternalTmsId
          ) {
            // Propagate the full fresh list to parent so resetOptionsHandler retains synced
            // locations after the dropdown closes. Skip the sync effect that would otherwise
            // overwrite filteredLocations back to the full fresh list during this render.
            skipNextLocationsSyncRef.current = true;
            setLocations?.(freshLocations);
            // Show only the matched results in the dropdown, not the full fresh list
            handleFilteredLocationsChange(matched);
            toast({
              description: 'Locations synced from GlobalTranz address book.',
              variant: 'success',
            });
            return mapLocationsToSelectOptions(matched, true);
          }
        }
      }
    }

    return results;
  };

  useEffect(() => {
    if (watchedLocationID) {
      const selectedLoc = filteredLocations?.find(
        (loc) => loc.externalTMSID === watchedLocationID
      );
      if (!selectedLoc) {
        return;
      }

      Object.entries(selectedLoc).forEach(([key, value]) => {
        setValue(`${stop}.${key}` as FieldPath<NormalizedLoad>, value, {
          shouldDirty: aiDefaultValues,
        });
      });

      // Set openTime and closeTime from location dock hours if available; when empty use first option for open, last for close
      const defaultTimes = getDefaultTimes(stopTypeEnum);
      const openTime =
        selectedLoc.openTime || openTimeOptions[0] || defaultTimes.openTime;
      const closeTime =
        selectedLoc.closeTime ||
        (closeTimeOptions.length > 0
          ? closeTimeOptions[closeTimeOptions.length - 1]
          : defaultTimes.closeTime);

      setValue(`${stop}.openTime` as FieldPath<NormalizedLoad>, openTime, {
        shouldDirty: aiDefaultValues,
      });
      setValue(`${stop}.closeTime` as FieldPath<NormalizedLoad>, closeTime, {
        shouldDirty: aiDefaultValues,
      });
    }
  }, [
    watchedLocationID,
    filteredLocations,
    setValue,
    stop,
    aiDefaultValues,
    openTimeOptions,
    closeTimeOptions,
    stopTypeEnum,
  ]);

  // Fetch full location details when selected location has no defaultGroupId (e.g. from search).
  // Backend persists details so next time the same location is selected we skip this.
  useEffect(() => {
    if (!tmsID) {
      return;
    }

    const selectedLoc = filteredLocations?.find(
      (loc) => loc.externalTMSID === watchedLocationID
    );

    // Return early if the location has no External TMS ID or a default group ID
    if (!selectedLoc?.externalTMSID || !!selectedLoc?.defaultGroupId) {
      return;
    }

    const customerExternalTmsId = watchedCustomer?.externalTMSID;
    const isAlreadyFetching =
      locationDetailsFetchInFlightRef.current === watchedLocationID;
    const isAlreadySelected =
      lastSelectedLocationIdRef.current === watchedLocationID;
    if (!customerExternalTmsId || isAlreadyFetching || isAlreadySelected) {
      return;
    }

    locationDetailsFetchInFlightRef.current = watchedLocationID;
    lastSelectedLocationIdRef.current = watchedLocationID;

    getLocationDetails(
      tmsID,
      selectedLoc.externalTMSID,
      customerID,
      customerExternalTmsId
    )
      .then((res) => {
        if (res.isErr()) {
          toast({
            description:
              'Unable to fetch location details. Please enter them manually.',
            variant: 'destructive',
          });
          return;
        }
        const loc = res.value.location;

        if (loc.externalTMSID !== watchedLocationID) {
          return;
        }

        // Fields that user can edit during API fetch
        const userEditableFields = ['phone', 'email', 'apptNote'];

        Object.entries(loc).forEach(([key, value]) => {
          // Get fresh dirty fields at the moment of applying values (not stale snapshot from effect start)
          // This ensures we catch any edits made during the async API call
          const currentStopDirtyFields = formMethods.formState.dirtyFields[
            stop
          ] as Undef<Record<string, boolean>>;

          // Skip overwriting fields that the user has manually edited
          if (
            userEditableFields.includes(key) &&
            currentStopDirtyFields?.[key]
          ) {
            return;
          }

          setValue(
            `${stop}.${key}` as FieldPath<NormalizedLoad>,
            value as never,
            { shouldDirty: aiDefaultValues }
          );
        });
        const defaultTimes = getDefaultTimes(stopTypeEnum);
        setValue(
          `${stop}.openTime` as FieldPath<NormalizedLoad>,
          loc.openTime || openTimeOptions[0] || defaultTimes.openTime,
          { shouldDirty: aiDefaultValues }
        );
        setValue(
          `${stop}.closeTime` as FieldPath<NormalizedLoad>,
          loc.closeTime ||
            (closeTimeOptions[closeTimeOptions.length - 1] ??
              defaultTimes.closeTime),
          { shouldDirty: aiDefaultValues }
        );
        setFilteredLocations((prev) =>
          prev
            ? prev.map((l) => (l.externalTMSID === loc.externalTMSID ? loc : l))
            : prev
        );

        // Let the user know details were updated successfully
        toast({
          description:
            'Location details updated. Please review auto-filled fields.',
          variant: 'success',
        });
      })
      .finally(() => {
        // Only clear the lock if this request is still the current one
        // (prevents older requests from clearing the lock for newer in-flight requests)
        if (locationDetailsFetchInFlightRef.current === watchedLocationID) {
          locationDetailsFetchInFlightRef.current = null;
        }
      });
  }, [
    watchedLocationID,
    filteredLocations,
    tmsID,
    watchedCustomer,
    stop,
    openTimeOptions,
    closeTimeOptions,
    setValue,
    stopTypeEnum,
    toast,
    customerID,
    aiDefaultValues,
    formMethods.formState.dirtyFields,
  ]);

  // Re-validate close time when either open or close time changes so "Close must be after open" error updates/clears
  useEffect(() => {
    trigger(`${stop}.closeTime` as FieldPath<NormalizedLoad>);
  }, [trigger, stop, watchedOpenTime, watchedCloseTime]);

  // When open/close time are empty, default open to first dropdown value and close to last
  useEffect(() => {
    if (openTimeOptions.length === 0 || closeTimeOptions.length === 0) return;
    const firstOpen = openTimeOptions[0];
    const lastClose = closeTimeOptions[closeTimeOptions.length - 1];
    if (!watchedOpenTime && firstOpen) {
      setValue(`${stop}.openTime` as FieldPath<NormalizedLoad>, firstOpen, {
        shouldDirty: false,
      });
    }
    if (!watchedCloseTime && lastClose) {
      setValue(`${stop}.closeTime` as FieldPath<NormalizedLoad>, lastClose, {
        shouldDirty: false,
      });
    }
  }, [
    stop,
    openTimeOptions,
    closeTimeOptions,
    setValue,
    watchedOpenTime,
    watchedCloseTime,
  ]);

  // Clear externalTMSID when user manually edits address fields away from the
  // selected location. Skip the check if the location ID just changed—gives the
  // hydrate effect time to populate form fields before we compare them.
  useEffect(() => {
    const prevId = prevLocationIDForMismatchRef.current;

    // Update the ref for next render
    prevLocationIDForMismatchRef.current = watchedLocationID || null;

    if (!watchedLocationID) {
      return;
    }

    // Skip the mismatch check if the location ID just changed this render cycle.
    // The form fields still hold OLD values; the hydrate effect will populate
    // them with the new location's data. Clearing now would race the hydrate.
    if (prevId !== watchedLocationID) {
      return;
    }

    // Remove ID if address is write-in
    const selectedLoc = filteredLocations?.find(
      (loc) => loc.externalTMSID === watchedLocationID
    );
    if (!selectedLoc) {
      return;
    }

    // Fields to check for differences (case-insensitive comparison)
    const fieldsToCheck: Array<
      'name' | 'addressLine1' | 'addressLine2' | 'city' | 'state' | 'zipCode'
    > = ['name', 'addressLine1', 'addressLine2', 'city', 'state', 'zipCode'];

    const hasFieldDifferences = fieldsToCheck.some((field) => {
      const selectedValue = selectedLoc[field];
      const watchedValue = watchedLocObj?.[field];
      return (
        selectedValue &&
        watchedValue &&
        watchedValue?.toLowerCase() !== selectedValue?.toLowerCase()
      );
    });

    if (hasFieldDifferences) {
      setValue(`${stop}.externalTMSID` as FieldPath<NormalizedLoad>, '');
    }
  }, [
    watchedLocationID,
    filteredLocations,
    watchedLocObj,
    watchedLocObj?.name,
    watchedLocObj?.addressLine1,
    watchedLocObj?.addressLine2,
    watchedLocObj?.city,
    watchedLocObj?.state,
    watchedLocObj?.zipCode,
    setValue,
    stop,
  ]);

  const handleLocationCreated = (newLocation: TMSLocation) => {
    const updatedLocations = [...(locations || []), newLocation];
    handleFilteredLocationsChange(updatedLocations);

    if (setLocations) {
      setLocations(updatedLocations);
    }

    // Select the newly created location
    setValue(
      `${stop}.externalTMSID` as FieldPath<NormalizedLoad>,
      newLocation.externalTMSID || '',
      {
        shouldDirty: true,
      }
    );
  };

  return (
    <>
      <RHFDebounceSelect
        required={true}
        name={`${stop}.externalTMSID`}
        label='Name'
        control={control}
        errors={errors}
        data={filteredLocations}
        isLoading={isLoadingLocations}
        refreshHandler={handleRefreshLocations}
        fetchOptions={handleLocationSearch}
        resetOptionsHandler={() => {
          setFilteredLocations((prev) =>
            withCurrentSelectionInSearchResults(
              locations,
              prev,
              locations,
              String(
                getValues(`${stop}.externalTMSID` as FieldPath<NormalizedLoad>)
              )
            )
          );
        }}
        mapOptions={() => mapLocationsToSelectOptions(filteredLocations, true)}
        showSearchParamDropdown={false}
        searchFieldDefault='nameAddress'
        placeholder='Search by name or address'
        customActionButtons={
          effectiveTmsID ? (
            <button
              type='button'
              onClick={() => setIsLocationCreatorOpen(true)}
              className='shrink-0 py-2 px-1 hover:text-info-600 rounded-full transition-colors cursor-pointer'
              title='Create new location'
            >
              <Plus className='h-4 w-4' />
            </button>
          ) : undefined
        }
      />

      {effectiveTmsID && (
        <Typography variant='body-xs' className='text-neutral-400 -mt-1'>
          {`Can't find the location? Click `}
          <button
            type='button'
            onClick={() => setIsLocationCreatorOpen(true)}
            className='text-info-600 hover:underline cursor-pointer font-medium'
          >
            here
          </button>
          {` to search your GlobalTranz address book.`}
        </Typography>
      )}

      {isEditingLocation && <hr className='my-2' />}

      <RHFTextInput
        name={`${stop}.addressLine1`}
        label='Address Line 1'
        readOnly={true}
      />

      <RHFTextInput
        name={`${stop}.addressLine2`}
        label='Address Line 2'
        readOnly={true}
      />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <RHFTextInput name={`${stop}.city`} label='City' readOnly={true} />

        <RHFTextInput name={`${stop}.state`} label='State' readOnly={true} />
      </Grid>

      <RHFTextInput
        name={`${stop}.zipCode`}
        label='Zip Code'
        placeholder='12345'
        readOnly={true}
      />

      {(stop === LoadStopTypes.Pickup || stop === LoadStopTypes.Consignee) && (
        <LoadDateInput
          name={
            stop === LoadStopTypes.Pickup
              ? (`${stop}.readyTime` as FieldPath<NormalizedLoad>)
              : (`${stop}.mustDeliver` as FieldPath<NormalizedLoad>)
          }
          label={
            stop === LoadStopTypes.Pickup ? 'Pickup Date' : 'Delivery Date'
          }
          required
        />
      )}

      {isEditingLocation && <hr className='my-2' />}

      <Grid cols='2' gap='sm'>
        <div>
          <Typography variant='body-sm' weight='semibold' className='mb-1'>
            Open Time <span className='text-error-500'>*</span>
          </Typography>
          <Controller
            name={`${stop}.openTime` as FieldPath<NormalizedLoad>}
            control={control}
            rules={{ required: 'Open time is required' }}
            render={({ field }) => (
              <Select
                value={formatTimeForSelect(field.value)}
                onValueChange={field.onChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {openTimeOptions.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <ErrorMessage
            errors={errors}
            name={`${stop}.openTime` as FieldPath<NormalizedLoad>}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>

        <div>
          <Typography variant='body-sm' weight='semibold' className='mb-1'>
            Close Time <span className='text-error-500'>*</span>
          </Typography>
          <Controller
            name={`${stop}.closeTime` as FieldPath<NormalizedLoad>}
            control={control}
            rules={{
              required: 'Close time is required',
              validate: (closeVal) => {
                const openVal = getValues(
                  `${stop}.openTime` as FieldPath<NormalizedLoad>
                );
                if (!closeVal || !openVal) return true;
                const closeM = parseHHMMToMinutes(
                  typeof closeVal === 'string' ? closeVal : ''
                );
                const openM = parseHHMMToMinutes(
                  typeof openVal === 'string' ? openVal : ''
                );
                return (
                  closeM >= openM ||
                  'Close time must be same as or after open time'
                );
              },
            }}
            render={({ field }) => (
              <Select
                value={formatTimeForSelect(field.value)}
                onValueChange={field.onChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {closeTimeOptions.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <ErrorMessage
            errors={errors}
            name={`${stop}.closeTime` as FieldPath<NormalizedLoad>}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>
      </Grid>

      {/* Appointment notes are create-only: GTZ converts them to shipment notes on
          load creation. In edit mode they are read-only and visible in the Notes
          section history, so the field is intentionally hidden here. */}
      {isCreateMode && (
        <RHFTextInput
          name={`${stop}.apptNote`}
          label='Appointment Note'
          placeholder='Appointment Note'
        />
      )}

      <RHFTextInput
        name={`${stop}.phone`}
        label='Contact Phone'
        placeholder='(123) 456-7890'
        required={true}
      />

      <RHFTextInput
        name={`${stop}.email`}
        label='Contact Email'
        placeholder='email@example.com'
        inputType='email'
      />

      {/* Location Requirement Section */}
      <Grid cols='4' gap='sm' className='mx-0 w-full'>
        <div className='col-span-4'>
          <Typography
            variant='subtitle2'
            className='text-neutral-700 font-medium mb-4'
          >
            Location Requirement
          </Typography>
        </div>

        <div className='col-span-4'>
          <div className='mb-2'>
            <Controller
              name={`${stop}.apptRequired`}
              control={control}
              render={({ field }) => (
                <Flex align='center' gap='sm'>
                  <Checkbox
                    onCheckedChange={(checked) => {
                      field.onChange(checked === true);
                    }}
                    checked={field.value ? true : false}
                  />
                  <label
                    htmlFor={`${stop}.apptRequired`}
                    className='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-neutral-500'
                  >
                    Appointment Required
                  </label>
                </Flex>
              )}
            />
          </div>

          <div>
            <Controller
              name={`${stop}.apptConfirmedWithLocation`}
              control={control}
              render={({ field }) => (
                <Flex align='center' gap='sm'>
                  <Checkbox
                    onCheckedChange={(checked) => {
                      field.onChange(checked === true);
                    }}
                    checked={field.value ? true : false}
                    disabled={apptConfirmedWithLocationFromLoad}
                  />
                  <label
                    htmlFor={`${stop}.apptConfirmedWithLocation`}
                    className='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-neutral-500'
                  >
                    Appointment Confirmed With Location
                  </label>
                </Flex>
              )}
            />
          </div>
        </div>

        {/* Show Appointment # when Appointment Confirmed With Location is checked */}
        {watchedLocObj?.apptConfirmedWithLocation && (
          <div className='col-span-4'>
            <RHFTextInput
              name={`${stop}.apptNum`}
              label='Appointment #'
              placeholder='Appointment Number'
            />
          </div>
        )}
      </Grid>

      {/* Location Creator Dialog */}
      {effectiveTmsID && (
        <LocationCreatorDialog
          isOpen={isLocationCreatorOpen}
          onClose={() => setIsLocationCreatorOpen(false)}
          onLocationCreated={handleLocationCreated}
          tmsID={effectiveTmsID}
          stopType={
            stop === 'pickup' ? LoadStopTypes.Pickup : LoadStopTypes.Consignee
          }
          customerBK={watchedCustomer?.externalTMSID}
          initialCustomers={initialCustomers}
          initialIsLoadingCustomers={initialIsLoadingCustomers}
          onLocationsRefreshed={fetchLocations ?? handleRefreshLocations}
        />
      )}
    </>
  );
}

/**
 * When `locationSearchHandler` replaces the list with search hits, the form may still
 * reference a previous pick. Reuse `injectSelectedObject` (see RHFCustomerSelect) so the
 * selected row stays in the array for Radix — including when the API returns no matches.
 */
function withCurrentSelectionInSearchResults(
  updatedLocations: Maybe<TMSLocation[]>,
  previousFiltered: Maybe<TMSLocation[]>,
  parentLocationList: Maybe<TMSLocation[]>,
  selectedExternalTmsId: Undef<string>
): Maybe<TMSLocation[]> {
  if (selectedExternalTmsId == null || selectedExternalTmsId === '') {
    return updatedLocations;
  }

  const id = selectedExternalTmsId;
  const searchHits = updatedLocations ?? [];

  if (searchHits.some((loc) => String(loc.externalTMSID) === id)) {
    return updatedLocations;
  }

  const currentLocation =
    previousFiltered?.find((loc) => String(loc.externalTMSID) === id) ??
    parentLocationList?.find((loc) => String(loc.externalTMSID) === id);

  if (!currentLocation) {
    return updatedLocations;
  }

  return injectSelectedObject(currentLocation, searchHits);
}
